// Waiver Modal Stimulus Controller
// 
// Manages sequential display and submission of customer waivers in a Bootstrap modal.
// Each waiver contains questions that must be answered before proceeding to the next waiver.
//
// Waiver HTML managed by Customers::Waivers::WaiverModalComponent
// 
// WORKFLOW:
// 1. Shows first waiver form on modal open
// 2. Validates all required fields (checkboxes, text inputs)
// 3. Submits form data via AJAX to Rails API endpoint
// 4. On success, shows next waiver or closes modal if complete
//
import { Controller } from 'stimulus'
import { Modal } from 'bootstrap.native'
// @ts-ignore
import focusLock from 'dom-focus-lock'

const { I18n } = window

export default class extends Controller {
  static targets = ["modal", "waiver", "submitButton"]
  static values = { 
    url: String,
    totalWaivers: Number,
    currentWaiverIndex: Number
  }

  modal: any
  urlValue: string
  totalWaiversValue: number
  currentWaiverIndexValue: number
  waiverTargets: HTMLElement[]
  submitButtonTarget: HTMLButtonElement

  connect() {
    this.currentWaiverIndexValue = 0
    this.totalWaiversValue = this.waiverTargets.length
    this.modal = new Modal(this.element, {
      backdrop: 'static',
      keyboard: false,
    })

    focusLock.on(this.element)
    this.showCurrentWaiver()
    this.modal.show()

    // Force modal to be reinitialized if user clicks away before modal opens
    document.addEventListener('turbolinks:click', () => (this.modal = null), false)
  }

  disconnect() {
    if (this.modal) {
      this.modal.hide()
    }
    focusLock.off(this.element)
  }

  showCurrentWaiver() {
    if (this.currentWaiverIndexValue >= this.totalWaiversValue) {
      // No more waivers to display
      this.modal.hide()
      return
    }

    this.waiverTargets.forEach(waiver => {
      waiver.classList.add('d-none')
    })

    const currentWaiver = this.waiverTargets[this.currentWaiverIndexValue]
    currentWaiver.classList.remove('d-none')
  }

  submitForm(event) {
    event.preventDefault()

    const currentWaiver = this.waiverTargets[this.currentWaiverIndexValue]
    const form = currentWaiver.querySelector('form')
    
    if (!this.validateForm(form)) {
      this.showError(I18n.t("views.waivers.complete_agreements"))
      return
    }
    
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = I18n.t("views.waivers.saving")
    this.submitToAPI(form)
  }

  // Check if all waiver fields are checked or filled
  validateForm(form) {
    const requiredFields = form.querySelectorAll('input[required]')
    
    for (let field of requiredFields) {
      if(field.type === "checkbox") {
        if (!field.checked) {
          return false
        }
      } else if (!field.value.trim()) {
        return false
      }
    }
    
    return true
  }

  async submitToAPI(form) {
    try {
      const formData = new FormData(form)
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: formData
      })
      
      if (response.ok) {
        this.handleSubmissionSuccess()
      } else {
        const error = await response.json()
        this.handleSubmissionError(error)
      }
    } catch (error) {
      console.error('API submission error:', error)
      this.handleSubmissionError({ message: I18n.t("views.waivers.network_error") })
    }
  }

  // Move to next waiver or close modal
  handleSubmissionSuccess() {
    this.currentWaiverIndexValue++
    this.showCurrentWaiver()
  }

  handleSubmissionError(error) {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.textContent = I18n.t("views.waivers.i_agree")
    this.showError(error.message || I18n.t("views.waivers.generic_error"))
  }

  showError(message) {
    const currentWaiver = this.waiverTargets[this.currentWaiverIndexValue]
    const errorAlert = currentWaiver.querySelector('.alert-danger')
    errorAlert.querySelector('span').textContent = message
    errorAlert.classList.remove('d-none')
  }

  clearErrors() {
    const currentWaiver = this.waiverTargets[this.currentWaiverIndexValue]
    const errorAlert = currentWaiver.querySelector('.alert-danger')
    if (errorAlert) {
      errorAlert.classList.add('d-none')
    }
  }
}
